import { useToast } from "contexts/ToastContext";
import { useEffect, useState } from "react";
import { LoadingPageIcon } from "utils/constant";
import { useTranslation } from "react-i18next";

import { useParams } from "react-router-dom";
import { getStakeholder } from "services/getEmployees";
import { useAuth } from "contexts/AuthContext";
import { CanShow, LEGISLATIVE_DRAFTING_UI_RULES, TeamScope } from "utils/authRules";
import { getLegalAdvisory } from "services/legalAdvisoryService";
import LegalAdvisoryDetail from "./fragments/leftPanel";
import LegalAdvisoryRightPanel from "./fragments/rightPanel";
import StakeholderRightPanel from "./fragments/stakeholderRightPanel";
import DocumentViewerModal from "components/DocumentViewer";
import RegistrarLegislativeDraft from "./registrarLegislativeDraft";

const SingleLegalAdvisory = () => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [data, setData] = useState({
        isLoading: true,
        status: "",
        forward_status: "",
        messenger_name: "",
        letter_reference_number: "",
        uploaded_files: [] as any[],
        research_completion_letter_file: "",
    });
    const { id } = useParams<{ id: string }>();
    const contextData = useAuth();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getLegalAdvisory(id || "");
                let user = null;
                if (response.requested_by_id && response.requested_by !== "internal") {
                    try {
                        user = await getStakeholder(response.requested_by_id, contextData.token || contextData?.user?.access_token || "");
                    } catch (err) {
                        console.error("Error fetching stakeholder:", err);
                        showToast(t("FailedToFetchStakeholder") || "Failed to fetch stakeholder", "error");
                    }
                }
                setData({
                    isLoading: false,
                    ...response,
                    requested_by_user: user,
                });
            } catch (error) {
                console.error("Error fetching legal research request:", error);
                showToast(t("FailedToFetchLegalResearchRequest") || "Failed to fetch legal research request.", "error");
                setData((prev) => ({ ...prev, isLoading: false }));
            }
        };

        fetchData();
    }, [id, contextData.token, contextData?.user?.access_token, showToast, t]);

    if (data.isLoading)
        return (
            <div className="flex justify-center items-center h-64">
                {LoadingPageIcon}
            </div>
        );

    // Prosecutor actions on this request go to its team leader only.
    return (
        <TeamScope team={(data as any)?.prosecutor_assignments}>
            <>
                <div className="flex h-full bg-gray-100 p-2">
                    <LegalAdvisoryDetail request={data} />
                    {CanShow(LEGISLATIVE_DRAFTING_UI_RULES.edit_reference_number.value, data?.status, data?.forward_status) ?
                        <RegistrarLegislativeDraft request={data} /> :
                        contextData.user?.roles?.some((role: any) => role === "Registrar") ?
                            <div className="flex-1 h-fit bg-white mb-4 p-4 rounded-lg">
                                <div className="pb-2 border-b border-gray-200 mb-4">
                                    <h1>{t("LegalAdviceCompletionLetter") || "Legal Advice Completion Letter"}</h1>
                                </div>
                                {data?.uploaded_files?.find((document: any) => document.document_type === "legal_advice_file_completion_letter_file") &&
                                    <DocumentViewerModal
                                        label={t("LegalAdviceCompletionLetter") || "Legal Advice Completion Letter"}
                                        documents={data?.uploaded_files.filter((document: any) => document.document_type === "legal_advice_file_completion_letter_file")}
                                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                                    />
                                }
                            </div> :
                        contextData.user?.user_type === "external" &&
                        data?.forward_status === "assigned_to_prosecutor" &&
                        (data?.status === "Waiting for Additional Information" || data?.status === "additional_info_submitted") ?
                            <StakeholderRightPanel request={data} /> :
                            <LegalAdvisoryRightPanel request={data} />
                    }
                </div>
            </>
        </TeamScope>
    );
};

export default SingleLegalAdvisory;